<script setup lang="ts">
import type {
    UploadFile,
    UploadProps,
    UploadRawFile,
    UploadUserFile,
} from 'element-plus'

interface UploadResponse {
    data?: {
        url?: string
    }
    message?: string
}

interface UploadErrorResponse {
    message?: string
    errors?: Record<string, string[]>
}

const props = defineProps<{
    productId: number
    productOptionId: number
    productOptionValueId: number
}>()

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'result', file: string): void
}>()

const config = useRuntimeConfig()
const { locale, t } = useI18n()

const fileList = ref<UploadUserFile[]>([])
const uploadedUrl = ref('')
const uploadError = ref('')
const isUploading = ref(false)
const maxUploadSizeMb = 10
const uploadedPreviewList = computed(() => (uploadedUrl.value ? [uploadedUrl.value] : []))

const uploadUrl = computed(
    () => `${config.public.apiBase}/product-option-files`,
)

const uploadHeaders = computed(() => ({
    Accept: 'application/json',
    'Accept-Language': locale.value,
}))

const uploadData = computed(() => ({
    product_id: props.productId,
    product_option_id: props.productOptionId,
    product_option_value_id: props.productOptionValueId,
}))

const beforeUpload: UploadProps['beforeUpload'] = (
    rawFile: UploadRawFile,
) => {
    uploadError.value = ''
    uploadedUrl.value = ''

    const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/webp',
    ]

    if (!allowedTypes.includes(rawFile.type)) {
        uploadError.value = t('product_option_file_invalid_type')
        return false
    }

    const maxSize = maxUploadSizeMb * 1024 * 1024

    if (rawFile.size > maxSize) {
        uploadError.value = t('product_option_file_too_large', {
            size: maxUploadSizeMb,
        })
        return false
    }

    isUploading.value = true

    return true
}

const handleSuccess: UploadProps['onSuccess'] = (
    response: UploadResponse,
) => {
    isUploading.value = false

    const url = response.data?.url

    if (!url) {
        uploadedUrl.value = ''
        uploadError.value =
            response.message || t('product_option_file_missing_url')

        return
    }

    uploadedUrl.value = url
    uploadError.value = ''
}

const getUploadErrorMessage = (error: Error): string => {
    if (!error.message) {
        return t('product_option_file_upload_error')
    }

    try {
        const response = JSON.parse(error.message) as UploadErrorResponse
        const firstError = response.errors
            ? Object.values(response.errors).flat()[0]
            : null

        return firstError || response.message || error.message
    } catch {
        return error.message
    }
}

const handleError: UploadProps['onError'] = (error) => {
    isUploading.value = false
    uploadedUrl.value = ''

    uploadError.value = getUploadErrorMessage(error)
}

const handleRemove: UploadProps['onRemove'] = () => {
    uploadedUrl.value = ''
    uploadError.value = ''
    isUploading.value = false
}

const handleExceed: UploadProps['onExceed'] = () => {
    uploadError.value = t('product_option_file_one_file')
}

const handleChange: UploadProps['onChange'] = (
    uploadFile: UploadFile,
) => {
    if (uploadFile.status === 'ready') {
        uploadError.value = ''
    }
}

const submit = () => {
    if (!uploadedUrl.value || isUploading.value) {
        return
    }

    emit('result', uploadedUrl.value)
    emit('close')
}
</script>

<template>
    <div class="option-file-upload">
        <div class="option-file-upload__header">
            <h3>{{ $t('product_option_file_title') }}</h3>
            <p>{{ $t('product_option_file_description') }}</p>
        </div>

        <el-upload
            v-model:file-list="fileList"
            drag
            :action="uploadUrl"
            :headers="uploadHeaders"
            :data="uploadData"
            :limit="1"
            name="file"
            accept=".jpg,.jpeg,.png,.webp"
            :disabled="isUploading"
            :before-upload="beforeUpload"
            :on-success="handleSuccess"
            :on-error="handleError"
            :on-remove="handleRemove"
            :on-exceed="handleExceed"
            :on-change="handleChange"
        >
            <Icon
                name="lucide:upload-cloud"
                size="34"
                class="option-file-upload__icon"
            />

            <div class="el-upload__text">
                {{ $t('product_option_file_drop_text') }}
            </div>

            <template #tip>
                <div class="el-upload__tip">
                    {{ $t('product_option_file_tip', { size: maxUploadSizeMb }) }}
                </div>
            </template>
        </el-upload>

        <el-alert
            v-if="uploadError"
            :title="uploadError"
            type="error"
            :closable="false"
            show-icon
        />

        <el-alert
            v-if="isUploading"
            :title="$t('product_option_file_uploading')"
            type="info"
            :closable="false"
            show-icon
        />

        <div
            v-if="uploadedUrl"
            class="option-file-upload__success"
        >
            <el-image
                :src="uploadedUrl"
                :preview-src-list="uploadedPreviewList"
                preview-teleported
                fit="cover"
                class="option-file-upload__preview"
            >
                <template #error>
                    <Icon name="lucide:image-off" size="24" />
                </template>
            </el-image>

            <div>
                <strong>{{ $t('product_option_file_uploaded') }}</strong>
                <span>{{ $t('product_option_file_click_preview') }}</span>
                <el-link
                    :href="uploadedUrl"
                    target="_blank"
                    type="success"
                    :underline="false"
                >
                    <Icon
                        name="lucide:external-link"
                        class="me-1"
                    />

                    {{ $t('product_option_file_preview_uploaded') }}
                </el-link>
            </div>
        </div>

        <div class="option-file-upload__actions">
            <el-button
                round
                :disabled="isUploading"
                @click="emit('close')"
            >
                {{ $t('cancel') }}
            </el-button>

            <el-button
                type="primary"
                round
                :loading="isUploading"
                :disabled="!uploadedUrl"
                @click="submit"
            >
                {{ $t('product_option_file_use') }}
            </el-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.option-file-upload {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__header {
        h3 {
            margin: 0 0 4px;
            font-size: 1.1rem;
            line-height: 1.3;
        }

        p {
            margin: 0;
            color: var(--el-text-color-secondary);
            font-size: var(--fs--1);
            line-height: 1.45;
        }
    }

    &__icon {
        color: var(--el-color-primary);
    }

    :deep(.el-upload) {
        width: 100%;
    }

    :deep(.el-upload-dragger) {
        width: 100%;
        padding: 32px 20px;
    }

    :deep(.el-upload-list__item) {
        margin-top: 10px;
    }

    &__success {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border: 1px solid var(--el-color-success-light-7);
        border-radius: 8px;
        background: var(--el-color-success-light-9);
        color: var(--el-color-success);

        > div {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
        }

        strong {
            color: var(--el-text-color-primary);
            font-size: var(--fs--1);
        }

        span {
            color: var(--el-text-color-secondary);
            font-size: var(--fs--2);
        }
    }

    &__preview {
        width: 74px;
        height: 74px;
        flex: 0 0 74px;
        border-radius: 8px;
        overflow: hidden;
        background: white;
        box-shadow: 0 0 0 1px var(--el-border-color-lighter);
        cursor: zoom-in;

        :deep(.el-image__error) {
            color: var(--el-text-color-secondary);
            background: var(--el-fill-color-light);
        }
    }

    &__actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
